<template>
  <div class="flash-cards-screen" :class="{ 'flash-cards-screen--rtl': isRtl }">
    <!-- Hero Header (matching MIA Dashboard) -->
    <div class="screen-hero">
      <div class="screen-hero__content">
        <button class="screen-hero__back" @click="goBack">
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <div class="screen-hero__icon">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
          >
            <polygon points="12,2 2,7 12,12 22,7 12,2" />
            <polyline points="2,17 12,22 22,17" />
            <polyline points="2,12 12,17 22,12" />
          </svg>
        </div>
        <div class="screen-hero__text">
          <h1 class="screen-hero__title">{{ t("flash_cards") }}</h1>
          <p class="screen-hero__subtitle">{{ t("flash_cards_subtitle") }}</p>
        </div>
      </div>
      <div class="screen-hero__decoration">
        <div class="screen-hero__circle screen-hero__circle--1"></div>
        <div class="screen-hero__circle screen-hero__circle--2"></div>
      </div>
    </div>

    <!-- Active Uploads -->
    <ActiveUploadsWidget :feature-type="featureType" />

    <!-- Content -->
    <div class="screen-content">
      <!-- Create New Button (inline, not floating) -->
      <div class="create-section">
        <button class="create-btn" @click="navigateToCreate">
          <div class="create-btn__icon">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
          <div class="create-btn__text">
            <span class="create-btn__title">{{ t("create_new") }}</span>
            <span class="create-btn__desc">{{
              t("create_flash_cards_desc")
            }}</span>
          </div>
          <svg
            class="create-btn__arrow"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="screen-loading">
        <div class="spinner"></div>
        <span>{{ t("loading") }}</span>
      </div>

      <!-- Error -->
      <div v-else-if="error" class="screen-error">
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <p>{{ error }}</p>
        <button class="btn btn--primary" @click="fetchDecks">
          {{ t("try_again") }}
        </button>
      </div>

      <!-- Empty -->
      <div v-else-if="decks.length === 0" class="screen-empty">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1"
        >
          <polygon points="12,2 2,7 12,12 22,7 12,2" />
          <polyline points="2,17 12,22 22,17" />
          <polyline points="2,12 12,17 22,12" />
        </svg>
        <p>{{ t("no_flashcards_yet") }}</p>
        <span>{{ t("create_first_content") }}</span>
      </div>

      <!-- Decks Grid -->
      <div v-else>
        <h3 class="section-title">{{ t("your_decks") }}</h3>
        <div class="decks-grid">
          <DeckCard
            v-for="deck in decks"
            :key="deck.id"
            :deck="deck"
            @click="handleDeckClick"
            @menu="openMenu"
            @status-changed="handleStatusChanged"
          />
        </div>

        <!-- Load More -->
        <button
          v-if="hasMore"
          class="load-more-btn"
          :disabled="loadingMore"
          @click="loadMore"
        >
          <span v-if="loadingMore" class="spinner spinner--small"></span>
          <span v-else>{{ t("load_more") }}</span>
        </button>
      </div>
    </div>

    <!-- Menu Dialog -->
    <div v-if="showMenu" class="dialog-overlay" @click.self="closeMenu">
      <div class="menu-dialog">
        <button class="menu-item" @click="openEditDialog">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          {{ t("edit_name") }}
        </button>
        <button class="menu-item" @click="showStats">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <line x1="18" y1="20" x2="18" y2="10" />
            <line x1="12" y1="20" x2="12" y2="4" />
            <line x1="6" y1="20" x2="6" y2="14" />
          </svg>
          {{ t("view_statistics") }}
        </button>
      </div>
    </div>

    <!-- Edit Title Dialog -->
    <div
      v-if="showEditDialog"
      class="dialog-overlay"
      @click.self="closeEditDialog"
    >
      <div class="dialog">
        <h3 class="dialog__title">{{ t("edit_name") }}</h3>
        <input
          v-model="editTitle"
          type="text"
          class="dialog__input"
          :placeholder="t('enter_title')"
          maxlength="255"
        />
        <div class="dialog__actions">
          <button class="btn btn--secondary" @click="closeEditDialog">
            {{ t("cancel") }}
          </button>
          <button
            class="btn btn--primary"
            @click="saveTitle"
            :disabled="savingTitle"
          >
            {{ savingTitle ? t("loading") : t("save") }}
          </button>
        </div>
      </div>
    </div>

    <!-- Statistics Dialog -->
    <div
      v-if="showStatsDialog"
      class="dialog-overlay"
      @click.self="closeStatsDialog"
    >
      <div class="dialog stats-dialog">
        <h3 class="dialog__title">{{ t("deck_statistics") }}</h3>
        <div v-if="loadingStats" class="stats-loading">
          <div class="spinner"></div>
        </div>
        <div v-else-if="stats" class="stats-content">
          <div class="stat-item">
            <span class="stat-item__label">{{ t("total_cards") }}</span>
            <span class="stat-item__value">{{
              stats.total_cards || stats.totalCards || 0
            }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-item__label">{{ t("mastered") }}</span>
            <span class="stat-item__value stat-item__value--success">{{
              stats.mastered || 0
            }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-item__label">{{ t("learning") }}</span>
            <span class="stat-item__value stat-item__value--warning">{{
              stats.learning || 0
            }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-item__label">{{ t("new") }}</span>
            <span class="stat-item__value stat-item__value--info">{{
              stats.new || 0
            }}</span>
          </div>
        </div>
        <div class="dialog__actions">
          <button class="btn btn--primary" @click="closeStatsDialog">
            {{ t("close") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useMiaStore } from "../stores/miaStore";
import { miaRefreshService } from "../services/refreshService";
import {
  MiaFeatureType,
  MiaRefreshType,
  StatusColors,
  OperationStatus,
} from "../types";
import { useI18n } from "../i18n";
import ActiveUploadsWidget from "../components/common/ActiveUploadsWidget.vue";
import DeckCard from "../components/common/DeckCard.vue";

export default {
  name: "FlashCardsScreen",
  components: {
    ActiveUploadsWidget,
    DeckCard,
  },
  setup() {
    const router = useRouter();
    const miaStore = useMiaStore();
    const { t, isRtl } = useI18n();

    const featureType = MiaFeatureType.FLASH_CARDS;
    const page = ref(1);
    const loadingMore = ref(false);

    const showMenu = ref(false);
    const selectedDeck = ref(null);

    const showEditDialog = ref(false);
    const editTitle = ref("");
    const savingTitle = ref(false);

    const showStatsDialog = ref(false);
    const loadingStats = ref(false);
    const stats = ref(null);

    let unsubscribe = null;

    const decks = computed(() => miaStore.state.flashCardDecks);
    const loading = computed(() => miaStore.state.flashCardDecksLoading);
    const error = computed(() => miaStore.state.flashCardDecksError);
    const hasMore = computed(() => {
      const meta = miaStore.state.flashCardDecksMeta;
      if (!meta) return false;
      return meta.current_page < Math.ceil(meta.total / meta.per_page);
    });

    const fetchDecks = async () => {
      page.value = 1;
      await miaStore.fetchFlashCardDecks(1);
    };

    const loadMore = async () => {
      if (loadingMore.value) return;
      loadingMore.value = true;
      page.value++;
      await miaStore.fetchFlashCardDecks(page.value);
      loadingMore.value = false;
    };

    const formatDate = (dateString) => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    };

    const getStatusColor = (status) => {
      return StatusColors[status] || StatusColors.pending;
    };

    const getStatusBgColor = (status) => {
      const color = getStatusColor(status);
      return `${color}1a`;
    };

    const goBack = () => {
      router.push("/mia");
    };

    const navigateToCreate = () => {
      router.push({
        path: "/mia/create",
        query: { feature: MiaFeatureType.FLASH_CARDS },
      });
    };

    const navigateToDeck = (deck, status) => {
      const deckStatus = status || deck.status;
      if (deckStatus === OperationStatus.COMPLETED) {
        router.push({
          path: "/mia/flash-cards/study",
          query: { deckId: deck.id },
        });
      } else {
        router.push({
          path: "/mia/flash-cards/details",
          query: { deckId: deck.id, deckTitle: deck.title || deck.name },
        });
      }
    };

    const handleDeckClick = ({ deck, status }) => {
      navigateToDeck(deck, status);
    };

    const handleStatusChanged = ({ id, status }) => {
      // Update local deck status
      const index = miaStore.state.flashCardDecks.findIndex((d) => d.id === id);
      if (index > -1) {
        miaStore.state.flashCardDecks[index] = {
          ...miaStore.state.flashCardDecks[index],
          status,
        };
      }
    };

    const openMenu = (deck) => {
      selectedDeck.value = deck;
      showMenu.value = true;
    };

    const closeMenu = () => {
      showMenu.value = false;
    };

    const openEditDialog = () => {
      closeMenu();
      editTitle.value =
        selectedDeck.value?.title || selectedDeck.value?.name || "";
      showEditDialog.value = true;
    };

    const closeEditDialog = () => {
      showEditDialog.value = false;
      editTitle.value = "";
    };

    const saveTitle = async () => {
      if (!editTitle.value.trim()) {
        alert("Title is required");
        return;
      }

      savingTitle.value = true;
      const result = await miaStore.updateDeckTitle(
        selectedDeck.value.id,
        editTitle.value.trim()
      );
      savingTitle.value = false;

      if (result.success) {
        closeEditDialog();
        fetchDecks();
      } else {
        alert(result.error || "Failed to update title");
      }
    };

    const showStats = async () => {
      closeMenu();
      showStatsDialog.value = true;
      loadingStats.value = true;

      const result = await miaStore.getDeckStatistics(selectedDeck.value.id);

      if (result.success) {
        stats.value = result.data;
      } else {
        stats.value = null;
      }

      loadingStats.value = false;
    };

    const closeStatsDialog = () => {
      showStatsDialog.value = false;
      stats.value = null;
    };

    onMounted(() => {
      fetchDecks();
      unsubscribe = miaRefreshService.subscribe(
        MiaRefreshType.FLASH_CARDS,
        fetchDecks
      );
    });

    onUnmounted(() => {
      if (unsubscribe) unsubscribe();
    });

    return {
      featureType,
      decks,
      loading,
      error,
      hasMore,
      loadingMore,
      showMenu,
      showEditDialog,
      editTitle,
      savingTitle,
      showStatsDialog,
      loadingStats,
      stats,
      t,
      isRtl,
      fetchDecks,
      loadMore,
      formatDate,
      getStatusColor,
      getStatusBgColor,
      goBack,
      navigateToCreate,
      navigateToDeck,
      handleDeckClick,
      handleStatusChanged,
      openMenu,
      closeMenu,
      openEditDialog,
      closeEditDialog,
      saveTitle,
      showStats,
      closeStatsDialog,
    };
  },
};
</script>

<style scoped>
/* ========== FLASH CARDS SCREEN - UNIFIED DESIGN ========== */
@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.2;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ========== Page Container ========== */
.flash-cards-screen {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fffe 0%, #e8fff1 100%);
}

/* ========== Hero Header (matching MIA Dashboard) ========== */
.screen-hero {
  background: linear-gradient(135deg, #00c744 0%, #00a83b 50%, #008f32 100%);
  padding: 32px 20px;
  position: relative;
  overflow: hidden;
  border-radius: 0 0 30px 30px;
  box-shadow: 0 8px 32px rgba(0, 199, 68, 0.25);
}

.screen-hero__content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
}

.screen-hero__back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.screen-hero__back:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateX(-2px);
}

.screen-hero__icon {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  color: white;
}

.screen-hero__text {
  flex: 1;
}

.screen-hero__title {
  color: white;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 4px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.screen-hero__subtitle {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin: 0;
}

.screen-hero__decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
}

.screen-hero__circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
}

.screen-hero__circle--1 {
  width: 150px;
  height: 150px;
  top: -40px;
  right: -30px;
  animation: pulse 4s ease-in-out infinite;
}

.screen-hero__circle--2 {
  width: 100px;
  height: 100px;
  bottom: -20px;
  left: 10%;
  animation: pulse 5s ease-in-out infinite 1s;
}

/* ========== Content Area ========== */
.screen-content {
  flex: 1;
  padding: 24px 20px 60px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}

/* ========== Create New Button (Inline) ========== */
.create-section {
  margin-bottom: 24px;
}

.create-btn {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 20px 24px;
  background: white;
  border: 2px solid rgba(0, 196, 68, 0.15);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(0, 196, 68, 0.08);
}

.create-btn:hover {
  border-color: #00c444;
  box-shadow: 0 8px 24px rgba(0, 196, 68, 0.15);
  transform: translateY(-2px);
}

.create-btn__icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #00c744 0%, #00a83b 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 199, 68, 0.3);
}

.create-btn__text {
  flex: 1;
  text-align: left;
}

.create-btn__title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 4px;
}

.create-btn__desc {
  display: block;
  font-size: 13px;
  color: #6b7280;
}

.create-btn__arrow {
  color: #00c444;
  transition: transform 0.3s ease;
}

.create-btn:hover .create-btn__arrow {
  transform: translateX(4px);
}

/* ========== Section Title ========== */
.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin: 0 0 16px 0;
}

/* ========== Loading, Error, Empty States ========== */
.screen-loading,
.screen-error,
.screen-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  text-align: center;
  color: #6b7280;
  min-height: 300px;
  background: white;
  border-radius: 20px;
  border: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 4px 20px rgba(0, 196, 68, 0.05);
}

.screen-empty svg,
.screen-error svg {
  margin-bottom: 20px;
  opacity: 0.6;
  color: #9ca3af;
}

.screen-empty p,
.screen-error p {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
}

.screen-empty span {
  font-size: 14px;
  color: #9ca3af;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 196, 68, 0.15);
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.spinner--small {
  width: 20px;
  height: 20px;
  border-width: 2px;
  margin: 0;
}

/* ========== Decks Grid ========== */
.decks-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}

/* ========== Load More Button ========== */
.load-more-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 14px 28px;
  margin-top: 24px;
  background: white;
  border: 1px solid rgba(0, 196, 68, 0.2);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #00c444;
  cursor: pointer;
  transition: all 0.3s ease;
}

.load-more-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #f0fff5 0%, #e6ffee 100%);
  border-color: #00c444;
}

.load-more-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* ========== Dialog Overlay ========== */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.menu-dialog {
  background: white;
  border-radius: 16px;
  padding: 12px;
  min-width: 220px;
  border: 1px solid rgba(0, 196, 68, 0.1);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 18px;
  background: transparent;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.menu-item:hover {
  background: rgba(0, 196, 68, 0.08);
  color: #00c444;
}

.dialog {
  background: white;
  border-radius: 20px;
  padding: 28px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 196, 68, 0.1);
}

.dialog__title {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 20px 0;
}

.dialog__input {
  width: 100%;
  padding: 14px 18px;
  font-size: 15px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  margin-bottom: 20px;
  transition: all 0.2s ease;
  background: #fafafa;
  color: #1a1a2e;
}

.dialog__input::placeholder {
  color: #9ca3af;
}

.dialog__input:focus {
  outline: none;
  border-color: #00c444;
  background: white;
  box-shadow: 0 0 0 4px rgba(0, 196, 68, 0.1);
}

.dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.stats-dialog {
  max-width: 360px;
}

.stats-loading {
  display: flex;
  justify-content: center;
  padding: 32px;
}

.stats-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: #f9fafb;
  border: 1px solid rgba(0, 196, 68, 0.1);
  border-radius: 12px;
  transition: all 0.2s ease;
}

.stat-item:hover {
  background: rgba(0, 196, 68, 0.05);
  border-color: rgba(0, 196, 68, 0.2);
}

.stat-item__label {
  font-size: 14px;
  color: #6b7280;
}

.stat-item__value {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
}

.stat-item__value--success {
  color: #16a34a;
}
.stat-item__value--warning {
  color: #d97706;
}
.stat-item__value--info {
  color: #2563eb;
}

/* ========== Buttons ========== */
.btn {
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn--primary {
  background: linear-gradient(135deg, #00c444 0%, #00a83a 100%);
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.25);
}

.btn--primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 196, 68, 0.35);
}

.btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn--secondary {
  background: white;
  color: #374151;
  border: 1px solid #e5e7eb;
}

.btn--secondary:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

/* RTL Support */
.flash-cards-screen--rtl {
  direction: rtl;
  text-align: right;
}

.flash-cards-screen--rtl .screen-hero__back svg {
  transform: scaleX(-1);
}

.flash-cards-screen--rtl .create-btn__text {
  text-align: right;
}

.flash-cards-screen--rtl .create-btn__arrow {
  transform: scaleX(-1);
}

.flash-cards-screen--rtl .create-btn:hover .create-btn__arrow {
  transform: scaleX(-1) translateX(4px);
}

.flash-cards-screen--rtl .dialog__actions {
  justify-content: flex-start;
}

/* ========== DARK MODE SUPPORT ========== */
[data-bs-theme="dark"] .flash-cards-screen,
[data-theme="dark"] .flash-cards-screen,
.dark .flash-cards-screen {
  background: linear-gradient(135deg, #0f1419 0%, #1a2332 100%);
}

[data-bs-theme="dark"] .screen-hero,
[data-theme="dark"] .screen-hero,
.dark .screen-hero {
  background: linear-gradient(135deg, #00a83b 0%, #008f32 50%, #007a2b 100%);
}

[data-bs-theme="dark"] .screen-content,
[data-theme="dark"] .screen-content,
.dark .screen-content {
  color: #e5e7eb;
}

[data-bs-theme="dark"] .create-btn,
[data-theme="dark"] .create-btn,
.dark .create-btn {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.25);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .create-btn:hover,
[data-theme="dark"] .create-btn:hover,
.dark .create-btn:hover {
  background: #263548;
  border-color: #00c444;
}

[data-bs-theme="dark"] .create-btn__title,
[data-theme="dark"] .create-btn__title,
.dark .create-btn__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .create-btn__desc,
[data-theme="dark"] .create-btn__desc,
.dark .create-btn__desc {
  color: #94a3b8;
}

[data-bs-theme="dark"] .section-title,
[data-theme="dark"] .section-title,
.dark .section-title {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-loading,
[data-bs-theme="dark"] .screen-error,
[data-bs-theme="dark"] .screen-empty,
[data-theme="dark"] .screen-loading,
[data-theme="dark"] .screen-error,
[data-theme="dark"] .screen-empty,
.dark .screen-loading,
.dark .screen-error,
.dark .screen-empty {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.15);
  color: #94a3b8;
}

[data-bs-theme="dark"] .screen-empty p,
[data-bs-theme="dark"] .screen-error p,
[data-theme="dark"] .screen-empty p,
[data-theme="dark"] .screen-error p,
.dark .screen-empty p,
.dark .screen-error p {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .screen-empty span,
[data-theme="dark"] .screen-empty span,
.dark .screen-empty span {
  color: #64748b;
}

[data-bs-theme="dark"] .load-more-btn,
[data-theme="dark"] .load-more-btn,
.dark .load-more-btn {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.25);
  color: #00c444;
}

[data-bs-theme="dark"] .load-more-btn:hover:not(:disabled),
[data-theme="dark"] .load-more-btn:hover:not(:disabled),
.dark .load-more-btn:hover:not(:disabled) {
  background: #263548;
}

[data-bs-theme="dark"] .menu-dialog,
[data-theme="dark"] .menu-dialog,
.dark .menu-dialog {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .menu-item,
[data-theme="dark"] .menu-item,
.dark .menu-item {
  color: #e2e8f0;
}

[data-bs-theme="dark"] .menu-item:hover,
[data-theme="dark"] .menu-item:hover,
.dark .menu-item:hover {
  background: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .dialog,
[data-theme="dark"] .dialog,
.dark .dialog {
  background: #1e293b;
  border-color: rgba(0, 196, 68, 0.2);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

[data-bs-theme="dark"] .dialog__title,
[data-theme="dark"] .dialog__title,
.dark .dialog__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .dialog__input,
[data-theme="dark"] .dialog__input,
.dark .dialog__input {
  background: #0f172a;
  border-color: #334155;
  color: #f1f5f9;
}

[data-bs-theme="dark"] .dialog__input::placeholder,
[data-theme="dark"] .dialog__input::placeholder,
.dark .dialog__input::placeholder {
  color: #64748b;
}

[data-bs-theme="dark"] .dialog__input:focus,
[data-theme="dark"] .dialog__input:focus,
.dark .dialog__input:focus {
  background: #1e293b;
  border-color: #00c444;
}

[data-bs-theme="dark"] .stat-item,
[data-theme="dark"] .stat-item,
.dark .stat-item {
  background: #0f172a;
  border-color: rgba(0, 196, 68, 0.15);
}

[data-bs-theme="dark"] .stat-item:hover,
[data-theme="dark"] .stat-item:hover,
.dark .stat-item:hover {
  background: rgba(0, 196, 68, 0.1);
}

[data-bs-theme="dark"] .stat-item__label,
[data-theme="dark"] .stat-item__label,
.dark .stat-item__label {
  color: #94a3b8;
}

[data-bs-theme="dark"] .stat-item__value,
[data-theme="dark"] .stat-item__value,
.dark .stat-item__value {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .btn--secondary,
[data-theme="dark"] .btn--secondary,
.dark .btn--secondary {
  background: #334155;
  color: #e2e8f0;
  border-color: #475569;
}

[data-bs-theme="dark"] .btn--secondary:hover,
[data-theme="dark"] .btn--secondary:hover,
.dark .btn--secondary:hover {
  background: #475569;
  border-color: #64748b;
}

/* Responsive */
@media (max-width: 768px) {
  .screen-hero {
    padding: 24px 16px;
    border-radius: 0 0 24px 24px;
  }

  .screen-hero__title {
    font-size: 1.25rem;
  }

  .screen-hero__icon {
    width: 48px;
    height: 48px;
  }

  .screen-content {
    padding: 20px 16px 40px;
  }

  .create-btn {
    padding: 16px 20px;
  }

  .create-btn__icon {
    width: 44px;
    height: 44px;
  }

  .decks-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 14px;
  }

  .dialog {
    padding: 24px;
    margin: 16px;
  }
}

@media (max-width: 480px) {
  .decks-grid {
    grid-template-columns: 1fr;
  }

  .screen-hero__icon {
    display: none;
  }
}
</style>
